import {
  HttpInterceptorFn,
  HttpEvent,
  HttpRequest,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '../services/auth.service';
import { switchMap, take, catchError, finalize } from 'rxjs/operators';
import { EMPTY, throwError, Observable, Subject } from 'rxjs';
import { CommonService } from '../../shared/services/common.service';
import { SecurityService } from '../services/security.service';
import { AppService } from '../services/app.service';
import { environment } from '../../../environments/environment';

let isAuthErrorHandled = false;
let tokenRefreshInProgress = false;
const tokenRefreshComplete$ = new Subject<void>();

const handleWaitingRequest = (): Observable<HttpEvent<unknown>> => {
  return new Observable<HttpEvent<unknown>>(subscriber => {
    const subscription = tokenRefreshComplete$.subscribe({
      next: () => {
        subscriber.complete();
      },
      error: () => {
        subscriber.complete();
      },
    });
    return () => subscription.unsubscribe();
  });
};

const handleTokenRefresh = (
  authService: AuthService,
  appService: AppService
): Observable<HttpEvent<unknown>> => {
  tokenRefreshInProgress = true;

  return authService.getRefreshToken().pipe(
    take(1),
    switchMap(() => {
      return EMPTY;
    }),
    catchError(() => {
      if (!isAuthErrorHandled) {
        isAuthErrorHandled = true;
        localStorage.removeItem('user_data');
        localStorage.removeItem('user_role');
        localStorage.removeItem('Selected_Org_Data');
        localStorage.removeItem('auth_token');

        appService.handleGlobalAuthError();

        setTimeout(() => {
          isAuthErrorHandled = false;
        }, 3000);
      }

      return EMPTY;
    }),
    finalize(() => {
      tokenRefreshInProgress = false;
      tokenRefreshComplete$.next();
    })
  );
};

const handle401Error = (
  error: HttpEvent<unknown>,
  authService: AuthService,
  appService: AppService,
  isLoginEndpoint: boolean,
  isCurrentUserEndpoint: boolean,
  isRefreshTokenEndpoint: boolean,
  isSignOutEndpoint: boolean
): Observable<HttpEvent<unknown>> => {
  if (isSignOutEndpoint) {
    authService.clearAuthDataAndRedirect();
    return EMPTY;
  }

  if (isLoginEndpoint || isCurrentUserEndpoint || isRefreshTokenEndpoint) {
    return throwError(() => error);
  }

  if (
    localStorage.getItem('verify-user') ||
    localStorage.getItem('isOtpVerified')
  ) {
    return throwError(() => error);
  }

  if (tokenRefreshInProgress) {
    return handleWaitingRequest();
  }

  return handleTokenRefresh(authService, appService);
};

const validateAndCleanValue = (
  value: unknown,
  localKey: string,
  securityService: SecurityService,
  skipFields: string[],
  displayPath: string
): { clean: unknown; hasIssue: boolean } => {
  if (skipFields.includes(localKey)) {
    return { clean: value, hasIssue: false };
  }

  if (typeof value === 'string') {
    const result = securityService.validateInput(value);
    if (!result.isValid) {
      securityService.showSecurityAlert(
        result.issue || 'Suspicious content',
        displayPath
      );
      return { clean: undefined, hasIssue: true };
    }
    return { clean: value, hasIssue: false };
  }

  if (Array.isArray(value)) {
    let hasIssue = false;
    const cleaned = value
      .map((item, i) => {
        const res = validateAndCleanValue(
          item,
          localKey,
          securityService,
          skipFields,
          `${displayPath}[${i}]`
        );
        if (res.hasIssue) hasIssue = true;
        return res.clean;
      })
      .filter(item => item !== undefined);
    return { clean: cleaned, hasIssue };
  }

  if (value !== null && typeof value === 'object') {
    let hasIssue = false;
    const cleaned: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      const res = validateAndCleanValue(
        v,
        k,
        securityService,
        skipFields,
        `${displayPath}.${k}`
      );
      if (res.hasIssue) hasIssue = true;
      if (res.clean !== undefined) cleaned[k] = res.clean;
    }
    return { clean: cleaned, hasIssue };
  }

  return { clean: value, hasIssue: false };
};

const validateAndCleanRequestBody = (
  req: HttpRequest<unknown>,
  securityService: SecurityService,
  isSamlConfigEndpoint: boolean,
  isSignOutEndpoint: boolean
): HttpRequest<unknown> | null => {
  if (!(req.body && typeof req.body === 'object' && !isSamlConfigEndpoint)) {
    return req;
  }

  const skipValidationFields = isSignOutEndpoint
    ? ['sloUrl', 'idpName']
    : [
        'body',
        'profileImage',
        'message',
        '_modifiedFields',
        'graphImage',
        'graphImageTotal',
      ];

  let isSecurityIssueDetected = false;
  const cleanedBody: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(
    req.body as Record<string, unknown>
  )) {
    const res = validateAndCleanValue(
      value,
      key,
      securityService,
      skipValidationFields,
      key
    );
    if (res.hasIssue) isSecurityIssueDetected = true;
    if (res.clean !== undefined) cleanedBody[key] = res.clean;
  }

  return isSecurityIssueDetected ? null : req;
};

export const AuthInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const common = inject(CommonService);
  const securityService = inject(SecurityService);
  const appService = inject(AppService);
  const isLoginEndpoint = req.url.includes('sign-in');
  const isCurrentUserEndpoint = req.url.includes('get-current-user');
  const isRefreshTokenEndpoint = req.url.includes('validate-refresh-token');
  const isMoodleEndpoint =
    req.url.includes('dev-moodle.insightstobehavior.net') ||
    req.url.includes('stage-moodle.insightstobehavior.net');
  const isSamlConfigEndpoint = req.url.includes('samlconfiguration');
  const isSignOutEndpoint = req.url.includes('sign-out');

  const modifiedReq = validateAndCleanRequestBody(
    req,
    securityService,
    isSamlConfigEndpoint,
    isSignOutEndpoint
  );

  if (modifiedReq === null) {
    return EMPTY;
  }

  return authService.authState$.pipe(
    take(1),
    switchMap(() => {
      const authorizedReq = modifiedReq.clone({
        setHeaders: {
          Accept: 'application/json',
          ...(isMoodleEndpoint ? {} : securityService.getSecurityHeaders()),
        },
        withCredentials: true,
      });

      return next(authorizedReq).pipe(
        catchError(error => {
          if (!environment.production) {
            console.error('HTTP Error in AuthInterceptor:', error);
          }
          if (error.status === 401) {
            return handle401Error(
              error,
              authService,
              appService,
              isLoginEndpoint,
              isCurrentUserEndpoint,
              isRefreshTokenEndpoint,
              isSignOutEndpoint
            );
          }

          if (error.status === 403) {
            common.notify(
              'error',
              'Access denied. Please check your permissions.'
            );
            return EMPTY;
          }

          if (error.status === 429) {
            common.notify(
              'error',
              'A process is currently ongoing. Please try again in 1 minute.'
            );
            return EMPTY;
          }
          return throwError(() => error);
        })
      );
    })
  );
};
